import {
  Component,
  effect,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { AlertComponent } from '../../atoms/alert/alert.component';
import { GlobalsStore } from '../../../stores/globals.state';
import { units } from '../../../models/unit';
import { CategoryStore } from '../../../stores/category/category.state';
import { HeaderComponent } from '../../atoms/header/header.component';
import { getIconByIndicatorType, Indicator } from '../../../models/indicator';
import { ChipModule } from 'primeng/chip';
import { NgIf } from '@angular/common';
import { TagListComponent } from '../../molecules/tag-list/tag-list.component';
import { DividerModule } from 'primeng/divider';
import { FieldsetModule } from 'primeng/fieldset';
import { ButtonModule } from 'primeng/button';
import { MenuModule } from 'primeng/menu';
import { MenuItem, MessageService } from 'primeng/api';
import { IndicatorStore } from '../../../stores/indicator/indicator.state';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { BaseListComponent } from '../base-list/base-list.component';
import { ActivatedRoute, Router } from '@angular/router';
import { RoutingService } from '../../../services/routing.service';
import { MaskComponent } from '../mask/mask.component';
import { SidebarModule } from 'primeng/sidebar';
import { IndicatorDataListComponent } from '../../molecules/indicator-data-list/indicator-data-list.component';
import { IndicatorDataStore } from '../../../stores/indicatpr-data/indicator-data.state';
import { AccordionModule } from 'primeng/accordion';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { getState } from '@ngrx/signals';
import { ActiveUserStore } from '../../../stores/user/active-user.state';
import { DialogModule } from 'primeng/dialog';
import { PermissionService } from '../../../services/permission.service';

@Component({
  selector: 'app-indicator-detail',
  standalone: true,
  imports: [
    AlertComponent,
    HeaderComponent,
    ChipModule,
    NgIf,
    TagListComponent,
    DividerModule,
    FieldsetModule,
    ButtonModule,
    MenuModule,
    MaskComponent,
    SidebarModule,
    AccordionModule,
    OverlayPanelModule,
    IndicatorDataListComponent,
    DialogModule,
  ],
  templateUrl: './indicator-detail.component.html',
  styleUrl: './indicator-detail.component.scss',
})
export class IndicatorDetailComponent
  extends BaseListComponent<Indicator>
  implements OnInit, OnChanges
{
  readonly store = inject(IndicatorStore);
  readonly globalStore = inject(GlobalsStore);
  readonly categoryStore = inject(CategoryStore);
  readonly dataStore = inject(IndicatorDataStore);
  readonly activeUserStore = inject(ActiveUserStore);
  @Input() indicator: Indicator | null = null;
  @Input() displayIndicatorData = false;

  protected readonly units = units;
  protected readonly getIconByIndicatorType = getIconByIndicatorType;
  protected selectedIndicator: Indicator | null = null;
  protected selectedChild: Indicator | null = null;
  public displayChildOverlay = false;
  public hasRightsToModify = false;

  override menuItems: MenuItem[] | undefined;
  override sidebarId = 'indicator-root';
  override stepsNavigateBack = 1;

  constructor(
    router: Router,
    route: ActivatedRoute,
    messageService: MessageService,
    confirmationDialogService: ConfirmationDialogService,
    routingService: RoutingService,
    private permissionService: PermissionService
  ) {
    super(
      router,
      route,
      messageService,
      confirmationDialogService,
      routingService
    );

    effect((): void => {
      if (this.indicator) return;

      const globalState = getState(this.globalStore);
      const indicatorState = getState(this.store);
      this.selectedIndicator = this.globalStore.selectedIndicator();
      this.buildMenu();

      // select indicator for indicator-detail-edit-form after reload
      if (this.store.selectedItem() && !globalState.selectedIndicator) {
        setTimeout(() => {
          this.globalStore.setSelectedIndicator(this.store.selectedItem());
        });
      }

      if (
        this.selectedIndicator &&
        !this.store.items()[this.selectedIndicator._id]
      ) {
        this.selectedIndicator = null;
        queueMicrotask(() => {
          this.globalStore.setSelectedIndicator(null);
          this.globalStore.setSelectedChildIndicator(null);
        });
      }
      //this.selectedChild = this.globalStore.selectedChildIndicator();
    });
  }

  /**
   * Lifecycle hook that is called after the component is initialized.
   * If the `indicator` property is defined, it clones the value into `selectedIndicator`.
   * Otherwise, it calls the `ngOnInit` method from the parent class.
   *
   * @return {void} This method does not return any value.
   */
  override ngOnInit(): void {
    if (this.indicator) {
      this.selectedIndicator = { ...this.indicator };
    } else {
      super.ngOnInit();
    }
  }

  /**
   * Selects a child indicator and updates the relevant state properties.
   *
   * @param {Indicator | null} child - The child indicator to be selected, or null to deselect any current child.
   * @return {void} No return value.
   */
  selectChild(child: Indicator | null) {
    if (
      child &&
      this.selectedIndicator &&
      child.parent === this.selectedIndicator._id
    ) {
      this.selectedChild = child;
      this.displayChildOverlay = !!child;
    } else if (!child) {
      this.selectedChild = null;
      this.displayChildOverlay = false;
    } else {
      console.warn('Invalid child-parent relationship detected');
      return;
    }
  }

  /**
   * A lifecycle hook that is triggered when Angular detects changes to the input-bound properties of a directive or component.
   * It processes changes to the `indicator` property and updates the `selectedIndicator` accordingly.
   *
   * @param {SimpleChanges} changes - An object of the current and previous property values that have changed.
   * @return {void} This method does not return a value.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['indicator'])
      this.selectedIndicator =
        this.indicator || this.globalStore.selectedIndicator();
    this.buildMenu();
  }

  /**
   * Builds and initializes the menu items based on the selected indicator's properties.
   *
   * This method creates and updates the menu system dynamically according to the current state
   * of the selected indicator, such as whether it is locked or unlocked. It assigns commands
   * to menu actions like editing, locking/unlocking, deleting, and closing the indicator.
   * Changes in the menu are propagated through the state updates.
   *
   * @return {void} Does not return a value. The method modifies the menuItems property of the class.
   */
  buildMenu(): void {
    const indicatorId = this.selectedIndicator?._id;
    const isLocked = this.selectedIndicator?.locked;
    if (!this.selectedIndicator) return;

    this.menuItems = [
      {
        visible: this.permissionService.checkIndicatorRights(
          this.selectedIndicator.category,
          'edit'
        ),
        label: 'bearbeiten',
        icon: 'fas fa-pen',
        routerLink: [
          '/data-backend',
          {
            outlets: {
              bearbeiten: ['indikator', indicatorId, 'general'],
            },
          },
        ],
      },
      {
        visible:
          !!this.selectedIndicator &&
          this.permissionService.checkIndicatorRights(
            this.selectedIndicator.category,
            isLocked ? 'unlock' : 'lock'
          ),
        label: isLocked ? 'freigeben' : 'sperren',
        icon: isLocked ? 'fas fa-unlock' : 'fas fa-lock',
        command: (event: any) => {
          if (this.selectedIndicator) {
            this.selectedIndicator.locked = !this.selectedIndicator.locked;
            this.store.update(this.selectedIndicator);
            this.buildMenu();
          }
        },
      },
      {
        visible:
          !!this.selectedIndicator &&
          this.permissionService.checkIndicatorRights(
            this.selectedIndicator.category,
            'remove'
          ),
        label: 'löschen',
        icon: 'fas fa-trash',
        command: (event: any) => {
          if (this.selectedIndicator !== null) {
            this.store.singleSelection(this.selectedIndicator._id);
            setTimeout(() => {
              if (this.selectedIndicator) {
                this.confirmationDialogService.confirmIndicatorDeletion(
                  event,
                  this.selectedIndicator,
                  () => {
                    if (this.selectedIndicator) {
                      this.remove(this.selectedIndicator);
                      this.globalStore.setSelectedIndicator(null);
                    }
                  }
                );
              }
            });
          }
        },
      },
      {
        visible: !!this.selectedIndicator,
        label: 'schließen',
        icon: 'fas fa-times',
        command: (event: any) => {
          this.closeIndicator();
        },
      },
    ];
    this.hasRightsToModify =
      this.menuItems.filter(item => item.visible).length > 1;
  }

  /**
   * Closes the currently selected indicator by setting it to null in the global store.
   * This action effectively deselects any active indicator.
   *
   * @return {void} Does not return a value.
   */
  closeIndicator(): void {
    this.globalStore.setSelectedIndicator(null);
  }
}
